import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Grid, Typography, makeStyles } from '@material-ui/core';
import CustomModal from '../customModal';
import SelectField from '../selectField';
import { isArrayEmpty } from '../../../utility/helpers';

const useStyles = makeStyles(({ spacing }) => ({
  root: {
    height: 306,
  },
  rootSingleSelect: {
    height: 'auto',
  },
  description: {
    marginBottom: spacing(4),
  },
  selectableUsers: {
    width: '100%',
  },
  selectedUsersViewport: {
    maxHeight: 180,
  },
}));

const AssignUsersDialog = ({
  translations,
  isOpened,
  isMulty = false,
  initialValue = undefined,
  shouldResetSelection = false,
  shouldReturnOption = false,
  allUsers,
  onCancel,
  onSave,
}) => {
  const classes = useStyles();
  const emptySelection = isMulty ? [] : null;

  const initialSelection = useMemo(() => {
    if (isMulty) {
      return Array.isArray(initialValue) ? initialValue : [];
    }
    return initialValue ?? null;
  }, [initialValue, isMulty]);

  const [users, setUsers] = useState(initialSelection);

  const isSelectionEmpty = isMulty ? isArrayEmpty(users) : users == null;

  const handleSelect = selectedUsers => setUsers(selectedUsers);

  const handleSave = async () => {
    await onSave(users);
    if (shouldResetSelection) {
      setUsers(isMulty ? [] : null);
    }
  };

  const handleCancel = () => {
    if (!isMulty && initialValue && !shouldResetSelection) {
      setUsers(initialSelection);
    } else {
      setUsers(emptySelection);
    }
    onCancel();
  };

  useEffect(() => {
    if (initialValue === undefined) {
      return;
    }
    setUsers(initialSelection);
  }, [initialValue, initialSelection]);

  return (
    <CustomModal
      title={translations.title}
      isOpened={isOpened}
      isConfirmDisabled={isSelectionEmpty}
      confirmButtonLabel={translations.create}
      closeButtonLabel={translations.cancel}
      onClose={handleCancel}
      onConfirm={handleSave}
      isMedium
    >
      <Grid
        container
        className={classNames(classes.root, {
          [classes.rootSingleSelect]: !isMulty,
        })}
      >
        <Grid item xs={12}>
          {translations.description && (
            <Typography className={classes.description}>
              {translations.description}
            </Typography>
          )}
          <SelectField
            className={classes.selectableUsers}
            selectedUsersViewportClass={classNames({
              [classes.selectedUsersViewport]: isMulty,
            })}
            label={translations.label}
            placeholder={translations.placeholder}
            value={users}
            options={allUsers}
            parser={{
              value: 'id',
              label: 'first_name',
            }}
            isMulti={isMulty}
            onChange={handleSelect}
            shouldReturnOption={shouldReturnOption}
            isUser
          />
        </Grid>
      </Grid>
    </CustomModal>
  );
};

AssignUsersDialog.propTypes = {
  translations: PropTypes.object.isRequired,
  isOpened: PropTypes.bool.isRequired,
  allUsers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isMulty: PropTypes.bool,
  shouldReturnOption: PropTypes.bool,
  shouldResetSelection: PropTypes.bool,
  initialValue: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default AssignUsersDialog;
